import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { resolverRol } from '../components/resolverRol';
import { salirDePin } from '../lib/pinSession';
import { modoPin, leerMeta, haySesionPinGuardada } from '../lib/pinStorage';

// App key en app_memberships (enum vls_app). Define el módulo del hub.
const APP = 'bitacora';
// Rol de la cuenta técnica del ingreso con PIN (membresía 'bitacora').
const ROL_PIN = 'kiosco';
// Roles viejos que valen lo mismo que el nuevo (transicion de roles unificados).
export const normalizarRol = (rol) => (rol === 'cables_pin' ? 'kiosco' : rol);
const REVISION_PIN_MS = 60 * 1000;
// Cuenta del kiosko del parque (auto-login por IP confiable).
export const KIOSK_EMAIL = 'kiosko@vlscr.com';

// La sesión PIN vale hasta la medianoche de Costa Rica (meta.expiraEl). Sin
// meta no hay forma de saber cuándo vence: se trata como vencida.
const pinVencida = () => {
  const meta = leerMeta();
  return !meta || !(Number(meta.expiraEl) > Date.now());
};

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // role === undefined  → todavía no resuelto
  // role === null        → resuelto, SIN membership en esta app
  // role === 'admin'|... → resuelto, con rol
  const [role, setRole] = useState(undefined);
  const [loading, setLoading] = useState(true);
  // true cuando la consulta del rol FALLÓ y resolverRol agotó su reintento, no
  // cuando contestó que la persona no es miembro. Señal aparte para que el gate
  // no confunda "no sabemos" con "no tiene acceso" y le muestre un sin-acceso
  // definitivo a alguien que solo tiene mala conexión.
  const [rolError, setRolError] = useState(false);
  // Id del usuario para el que `role` ya está (o está por quedar) resuelto. Va
  // en un ref, no en el state `user`: onAuthStateChange se suscribe una sola vez
  // dentro del useEffect de deps [], así que su closure quedaría atada para
  // siempre al `user` del primer render (null) si comparara contra el state.
  const resolvedUserIdRef = useRef(null);
  // Sube en cada resolucion del rol. Cada llamada se queda con su numero y,
  // al volver del await, si el contador ya avanzo es que hay otra mas nueva:
  // descarta lo suyo. Sin esto, dos resoluciones cruzadas dejan mandando a la
  // que contesta ultima, que puede ser la vieja.
  const peticionRolRef = useRef(0);
  // Datos del ingreso por PIN (nombre mostrado, vencimiento). Solo se llenan en
  // modo PIN; en modo normal quedan en null.
  const [pinMeta, setPinMeta] = useState(null);
  // true mientras salirDePin() corre: su signOut emite SIGNED_OUT y la
  // suscripción no debe volver a lanzarlo (bucle).
  const saliendoPinRef = useRef(false);
  // true cuando se abrió en modo PIN sin red y la sesión guardada sigue siendo
  // recuperable (ver init y el evento online).
  const pinRecuperableRef = useRef(false);

  // El rol vive en app_memberships (modelo multi-app). app_role_of(app) resuelve
  // el rol del usuario autenticado para esta app, respetando RLS vía auth.uid().
  // Pasa por resolverRol para distinguir "la consulta no contestó" de "contestó
  // que no hay fila", que antes colapsaban en el mismo null: una caída de red de
  // medio segundo daba el mismo resultado que una falta real de permisos.
  const loadRole = async () => {
    const mia = ++peticionRolRef.current;
    const { estado, rol } = await resolverRol(
      () => supabase?.rpc?.('app_role_of', { p_app: APP }),
    );
    if (mia !== peticionRolRef.current) return; // llego tarde
    if (estado === 'error') {
      setRolError(true);
      return;
    }
    setRolError(false);
    setRole(normalizarRol(rol));
  };

  // Reintento manual desde el botón de la pantalla de error. Solo tiene sentido
  // con sesión activa: es el único caso en que rolError puede quedar en true.
  const reintentarRol = () => {
    setRolError(false);
    setRole(undefined);
    loadRole();
  };

  // Lo llama tanto init() (incluido el camino de kiosko) como la suscripción de
  // auth. Escribe el ref acá, en el único lugar que resuelve, para que la guarda
  // de la suscripción tenga contra qué comparar.
  const onChange = (event, session) => {
    setUser(session?.user ?? null);
    setLoading(false);
    setRolError(false);
    resolvedUserIdRef.current = session?.user?.id ?? null;
    setPinMeta(session?.user && modoPin() ? leerMeta() : null);
    if (session?.user) {
      setRole(undefined);
      loadRole();
    } else {
      setRole(null);
    }
  };

  // Cierra la sesión PIN (signOut local + limpieza del modo) y deja la app sin
  // sesión y en modo normal, para que el login SSO funcione después. Solo usa
  // setters y refs (estables), así que la comparten el efecto y signOut.
  const cerrarPin = async () => {
    saliendoPinRef.current = true;
    try {
      await salirDePin();
    } finally {
      saliendoPinRef.current = false;
    }
    resolvedUserIdRef.current = null;
    setUser(null);
    setRole(null);
    setRolError(false);
    setPinMeta(null);
    setLoading(false);
  };

  useEffect(() => {
    let active = true;

    const init = async () => {
      // 0. Arranque en modo PIN: la sesión vive en el almacén aislado. Se sigue
      //    solo si hay sesión y no venció; si no, se limpia el modo y se cae al
      //    flujo normal (kiosko / login SSO). Nunca pasa por la guarda de
      //    kiosko: la cuenta técnica del PIN no es el kiosko.
      if (modoPin()) {
        const { data: { session: sesionPin } = {} } = (await supabase?.auth?.getSession()) || { data: {} };
        if (sesionPin?.user && !pinVencida()) {
          if (active) onChange(null, sesionPin);
          return;
        }
        // Sin red con el access token vencido, auth-js devuelve session null pero
        // CONSERVA el refresh token. Si todavía hay sesión guardada y el día no
        // venció, no se destruye: la app queda sin sesión por ahora y se
        // recupera sola al volver la red (evento online / refresh automático).
        if (!sesionPin?.user && !pinVencida() && haySesionPinGuardada()) {
          pinRecuperableRef.current = true;
          if (active) onChange(null, null);
          return;
        }
        if (!active) return;
        saliendoPinRef.current = true;
        try {
          await salirDePin();
        } finally {
          saliendoPinRef.current = false;
        }
      }
      // 1. Sesión existente (cookie SSO .vlscr.net)
      const { data: { session } = {} } = (await supabase?.auth?.getSession()) || { data: {} };
      // 2. ¿IP confiable del parque? (GET liviano, no loguea)
      let isTrusted = false;
      try {
        const r = await fetch('/api/kiosk-session', { method: 'GET' });
        if (r?.ok) isTrusted = !!(await r.json())?.trusted;
      } catch (e) { /* sin red al edge → tratar como no confiable */ }

      if (session?.user) {
        // Sesión de kiosko fuera de la red → revocar (no debe persistir off-site)
        if (session.user.email === KIOSK_EMAIL && !isTrusted) {
          await supabase?.auth?.signOut();
          if (active) onChange(null, null);
          return;
        }
        if (active) onChange(null, session);
        return;
      }

      // 3. Sin sesión + IP confiable → auto-login de kiosko
      if (isTrusted) {
        try {
          const r = await fetch('/api/kiosk-session', { method: 'POST' });
          if (r?.ok) {
            const { access_token, refresh_token } = await r.json();
            const { data, error: setErr } = await supabase?.auth?.setSession({ access_token, refresh_token });
            if (!setErr && active) { onChange(null, data?.session ?? null); return; }
          }
        } catch (e) { /* cae a login manual */ }
      }
      // 4. Sin sesión + fuera de la red → login SSO
      if (active) onChange(null, null);
    };

    init();

    // Revisión del vencimiento de la sesión PIN: cada 60 s y al volver a la
    // pestaña (una tablet dormida no corre timers). No-op fuera del modo PIN.
    const revisarPin = () => {
      if (!active || saliendoPinRef.current || !modoPin()) return;
      if (pinVencida()) cerrarPin();
    };
    const intervalo = setInterval(revisarPin, REVISION_PIN_MS);
    const alVolver = () => {
      if (document.visibilityState !== 'hidden') revisarPin();
    };
    document.addEventListener('visibilitychange', alVolver);

    // Al volver la red, si la sesión PIN quedó recuperable (apertura sin red),
    // se pide la sesión otra vez: auth-js refresca con el token guardado.
    const alVolverRed = async () => {
      if (!active || !pinRecuperableRef.current || !modoPin()) return;
      try {
        const { data: { session } = {} } = (await supabase?.auth?.getSession()) || { data: {} };
        // Se revalida tras el await: un TOKEN_REFRESHED pudo autenticar antes y
        // bajar la bandera; repetir onChange duplicaría setUser y loadRole.
        if (active && pinRecuperableRef.current && session?.user && modoPin()) {
          pinRecuperableRef.current = false;
          onChange(null, session);
        }
      } catch (e) { /* sigue recuperable */ }
    };
    window.addEventListener('online', alVolverRed);

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      // Sesión PIN cerrada de verdad (refresh token rechazado/revocado, signOut
      // desde otra pestaña): se restaura el modo normal en vez de quedar con un
      // marcador huérfano. SOLO con SIGNED_OUT: INITIAL_SESSION(null) es el
      // arranque (lo resuelve init) y no destruye una sesión recuperable.
      if (event === 'SIGNED_OUT' && modoPin() && !saliendoPinRef.current) {
        pinRecuperableRef.current = false;
        cerrarPin();
        return;
      }
      if (session?.user) pinRecuperableRef.current = false;
      if (event === 'TOKEN_REFRESHED' && !session && !modoPin()) {
        supabase?.auth?.signOut();
        resolvedUserIdRef.current = null;
        setUser(null);
        setRole(null);
        setRolError(false);
        setLoading(false);
        return;
      }
      // Misma persona que la ya resuelta (ej. TOKEN_REFRESHED CON sesión, que
      // Supabase dispara cada hora con autoRefreshToken:true, y los re-emits al
      // volver el foco a la pestaña): no es un cambio de sesión, no hay nada que
      // re-resolver. Sin esta guarda, el setRole(undefined) de onChange dejaba
      // roleResolved en false por un tick en CADA refresh, y el gate desmontaba y
      // remontaba toda la app autenticada para mostrar "Verificando acceso…",
      // perdiendo lo que hubiera sin guardar en cualquier formulario abierto.
      // Igual se refresca el objeto `user`, que trae el token nuevo.
      if ((session?.user?.id ?? null) === resolvedUserIdRef.current) {
        setUser(session?.user ?? null);
        setLoading(false);
        return;
      }
      onChange(event, session);
    });

    return () => {
      active = false;
      subscription?.unsubscribe();
      clearInterval(intervalo);
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('online', alVolverRed);
    };
  }, []);

  const signIn = async (email, password) => {
    try {
      // Un administrador que entra por el enlace "Soy de administración" mientras
      // hay una sesión PIN recuperable (marcador puesto, sin usuario) no debe
      // aterrizar en el almacén PIN: heredaría esPin y el nombre del operador
      // anterior. Se sale del PIN ANTES para que el login use la cookie normal.
      if (modoPin() && !user) {
        pinRecuperableRef.current = false;
        saliendoPinRef.current = true;
        try {
          await salirDePin();
        } finally {
          saliendoPinRef.current = false;
        }
        setPinMeta(null);
      }
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password });
      return { data, error };
    } catch (error) {
      return { error: { message: 'Error de red. Inténtalo de nuevo.' } };
    }
  };

  const signOut = async () => {
    // La cuenta técnica del PIN se comparte: un signOut global revocaría las
    // sesiones de todos. Con PIN solo se cierra la local.
    if (role === ROL_PIN || modoPin()) {
      await cerrarPin();
      return { error: null };
    }
    try {
      const { error } = await supabase?.auth?.signOut();
      if (!error) {
        // El ref se limpia junto con el state: si quedara con el id viejo y la
        // misma persona volviera a entrar, el SIGNED_IN se leería como "mismo
        // usuario ya resuelto" y se quedaría con el role null de acá.
        resolvedUserIdRef.current = null;
        setUser(null);
        setRole(null);
        setRolError(false);
      }
      return { error };
    } catch (error) {
      return { error: { message: 'Error de red. Inténtalo de nuevo.' } };
    }
  };

  // Defensa en profundidad: en modo PIN cualquier sesión es restringida, aunque
  // la membresía de la cuenta técnica se configurara con un rol más amplio.
  const esPin = role === ROL_PIN || (modoPin() && !!user);

  const value = {
    user,
    role,
    loading,
    isAuthenticated: !!user,
    roleResolved: role !== undefined,
    hasAccess: typeof role === 'string' && !!role,
    rolError,
    reintentarRol,
    isAdmin: role === 'admin',
    esPin,
    esKiosco: !esPin && user?.email === KIOSK_EMAIL,
    nombrePin: esPin ? pinMeta?.nombre ?? null : null,
    pinExpiraEl: esPin ? pinMeta?.expiraEl ?? null : null,
    signIn,
    signOut,
    userDisplayName: user?.email?.split('@')?.[0] || 'Usuario',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
